/**
 * Plugin Registry API Client
 * Fetches Claude marketplace plugins + manages installed workspace plugins.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const API_BASE = '/api/workspaces';

// Installed plugin (from our DB)
export interface Plugin {
  id: string;
  workspaceId: string;
  name: string;
  slug: string;
  description: string;
  content: string;
  category: string;
  isBuiltin: boolean;
  enabledByDefault: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// Marketplace plugin (from GitHub)
export interface MarketplacePlugin {
  name: string;
  description: string;
  category: string;
  version?: string;
  author?: { name: string; email?: string };
  source: string;
  tags?: string[];
  marketplace: string;
}

export interface MarketplaceSourceConfig {
  key: string;
  label: string;
  url: string;
  isBuiltin: boolean;
  disabled: boolean;
}

export interface MarketplaceResponse {
  plugins: MarketplacePlugin[];
  sources: MarketplaceSourceConfig[];
}

export interface CreatePluginRequest {
  name: string;
  slug?: string;
  description?: string;
  content: string;
  category?: string;
  enabledByDefault?: boolean;
  sortOrder?: number;
}

export interface UpdatePluginRequest {
  name?: string;
  description?: string;
  content?: string;
  category?: string;
  enabledByDefault?: boolean;
  sortOrder?: number;
}

// Fetch marketplace plugins (from Anthropic GitHub repos)
export function useMarketplacePlugins(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['marketplace-plugins', workspaceId],
    queryFn: async (): Promise<MarketplaceResponse> => {
      if (!workspaceId) return { plugins: [], sources: [] };
      const res = await fetch(`${API_BASE}/${workspaceId}/plugins/marketplace`);
      if (!res.ok) throw new Error(`Failed to fetch marketplace: ${res.statusText}`);
      return (await res.json()) as MarketplaceResponse;
    },
    enabled: !!workspaceId,
    staleTime: 15 * 60 * 1000, // 15 min cache
  });
}

// Fetch installed plugins (from our DB)
export function usePlugins(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['plugins', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return [];
      const res = await fetch(`${API_BASE}/${workspaceId}/plugins`);
      if (!res.ok) throw new Error(`Failed to fetch plugins: ${res.statusText}`);
      return (await res.json()) as Plugin[];
    },
    enabled: !!workspaceId,
  });
}

export function useCreatePlugin(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreatePluginRequest) => {
      const res = await fetch(`${API_BASE}/${workspaceId}/plugins`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Failed to install plugin: ${res.statusText}`);
      return (await res.json()) as Plugin;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plugins', workspaceId] }),
  });
}

export function useUpdatePlugin(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdatePluginRequest }) => {
      const res = await fetch(`${API_BASE}/${workspaceId}/plugins/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error(`Failed to update plugin: ${res.statusText}`);
      return (await res.json()) as Plugin;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plugins', workspaceId] }),
  });
}

export function useDeletePlugin(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_BASE}/${workspaceId}/plugins/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error(`Failed to uninstall plugin: ${res.statusText}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['plugins', workspaceId] }),
  });
}
